/* eslint-disable no-nested-ternary */
import { Route, Routes, Link, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
// DEVICE DETECT
import { isMobile } from "react-device-detect";
// ANT DESIGN COMPONENTS
import { Layout, Menu, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRYARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faBuilding,
	faBuildingUser,
	faUsers,
	faUserShield,
	faShieldHalved,
	faTools,
	faGears,
	faServer,
	faBuildingShield,
	faElevator,
	faFileCircleCheck,
	faChartGantt,
	faFileArchive,
	faPlugCircleCheck,
	faEthernet,
} from "@fortawesome/free-solid-svg-icons";
// SWITCH THEME
import { useThemeSwitcher } from "react-css-theme-switcher";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
	updateBreadcrumbTitle,
	updateUserLogged,
	updateTheme,
} from "../../redux/home/homeSlice";
import { updatePermissions } from "../../redux/users/userSlice";
import { useLazyGetCurrentLoggedUserQuery } from "../../redux/users/userAPI";
import { updateServer } from "../../redux/servers/serverSlice";
import { updateRenderComponent } from "../../redux/channels/channelSlice";
// COMPONENTS
import LayoutBreadcrumb from "./LayoutBreadcrumb";
import UsersPage from "../userManagement/users/UsersPage";
import OrganizationsPage from "../organizationManagement/organizations/OrganizationsPage";
import RolesPage from "../organizationManagement/roles/RolesPage";
import RootPermissionsPage from "../tools/rootPermissions/RootPermissionsPage";
import OffsetTypePage from "../tools/offsetType/OffsetTypePage";
import YourProfileDrawer from "./YourProfileDrawer";
import GlobalAlert from "./GlobalAlert";
import MonitoringPage from "../monitoringManagement/monitoring/MonitoringPage";
import MapViewPage from "../mapViewManagement/MapViewPage";
import NotFoundPage from "../unauthorized/NotFoundPage";
import BanksPage from "../edgeManagement/banks/BanksPage";
import ServersPage from "../edgeManagement/servers/ServersPage";
import LiftsPage from "../edgeManagement/lifts/LiftsPage";
import ConfigurationFiles from "../configurationManagement/configurationFiles/ConfigurationFilesPage";
import DefinitionsPage from "../configurationManagement/definitions/DefinitionsPage";
import OffsetsPage from "../configurationManagement/offsets/OffsetsPage";
import SpecialSwitchesPage from "../tools/specialSwitches/SpecialSwitchesPage";
import SpecialSwitchesCatalogPage from "../tools/specialSwitchesCatalog/SpecialSwitchesCatalogPage";
import DataBlocksPage from "../tools/dataBlocks/DataBlocksPage";
import IconDefinitionPage from "../tools/iconDefinition/IconDefinitionPage";
import PermissionsPage from "../tools/permissions/PermissionsPage";
import ReportGeneralPage from "../reports/generalReports/ReportGeneralPage";
import ReportFaultPage from "../reports/fault/ReportFaultPage";
import ReportPerformancePage from "../reports/performance/ReportPerformancePage";
import ReportOutOfServicePage from "../reports/outOfService/ReportOutOfServicePage";
import ReportTrafficPage from "../reports/traffic/ReportTrafficPage";
import ReportCarUsagePage from "../reports/carUsage/ReportCarUsagePage";
import ReportServicePage from "../reports/service/ReportServicePage";
import ReportBeneficialPage from "../reports/beneficial/ReportBeneficialPage";
import VendorsPage from "../tools/vendors/VendorsPage";
import AdaptersPage from "../edgeManagement/adapters/AdaptersPage";
import ChannelsPage from "../edgeManagement/channels/ChannelsPage";
import TermsAndConditions from "./TermsAndConditions";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
import validatePermission from "../../utils/validatePermissions";
// STYLES
import "../../styles/generalStyles.scss";
import logoBlack from "../../assets/logo/black-bg-lnlogo.png";
import logoWhite from "../../assets/logo/white-bg-lnlogo.png";
import mobileLogo from "../../assets/logo/lnLogo.png";

const { Header, Content, Footer, Sider } = Layout;
const showMapView = false;

const App = () => {
	// ************************************************ */
	// LOCAL STORAGE AND VARIABLES ******************** */
	const { switcher, themes } = useThemeSwitcher();
	const dispatch = useDispatch();
	const location = useLocation();
	const [collapsed, setCollapsed] = useState<boolean>(false);
	const [t] = useTranslation("global");
	const { theme, userLogged } = useSelector((state: any) => state.home);
	const { permissions } = useSelector((state: any) => state.user);
	const [current, setCurrent] = useState<any>("");
	const [openKeys, setOpenKeys] = useState([""]);
	const [isLoading, setIsLoading] = useState(false);
	const [localShowFullView, setLocalShowFullView] = useState(false);
	const rootSubmenuKeys = [
		"users",
		"monitoring",
		"map-view/map",
		"roles",
		"organizations",
		"edge-management/servers",
		"edge-management/banks",
		"edge-management/lifts",
		"edge-management/adapters",
		"edge-management/channels",
		"root-permissions",
		"offset-type-configuration",
		"configuration-catalog",
		"configuration-catalog/definitions",
		"configuration-catalog/offsets",
		"reports/report-general",
		"reports/report-fault",
		"reports/report-traffic",
		"reports/report-car-user",
		"reports/report-service",
		"reports/report-beneficial",
		"reports/report-performance",
		"reports/report-out-of-service",
		"tools/data-blocks",
		"tools/root-permissions",
		"tools/offset-type-configuration",
		"tools/special-switches-configuration",
		"tools/special-switches-catalog-configuration",
		"tools/vendors",
		"tools/icon-definition",
		"tools/permissions",
	];
	const { showFullView } = useSelector((state: any) => state.monitoring);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerGetCurrentLoggedUserQuery,
		{
			isLoading: getCurrentLoggedUserIsLoading,
			isError: getCurrentLoggedUserIsError,
		},
	] = useLazyGetCurrentLoggedUserQuery();
	// ************************************************* */
	// FUNCTIONS *************************************** */
	const showIsLoading = () => {
		setIsLoading(true);
		setTimeout(() => {
			setIsLoading(false);
		}, 300);
	};

	const handleChangeHeaderTitle = (title: string) => {
		localStorage.setItem("headerTitle", title);
		dispatch(updateBreadcrumbTitle(title));
	};

	const getCurrentLoggedUser = async () => {
		// Online authentication
		const token = await GETJwtToken();
		const response = await triggerGetCurrentLoggedUserQuery({
			token,
		}).unwrap();
		if (response && response.data && response.data.user) {
			dispatch(updateUserLogged(response.data.user));
			if (
				response.data.user.pref_theme &&
				response.data.user.pref_theme !== ""
			) {
				switcher({ theme: themes[response.data.user.pref_theme] });
				localStorage.setItem("theme", response.data.user.pref_theme);
				dispatch(updateTheme(response.data.user.pref_theme));
			}
			if (response.data.user.role && response.data.user.role.menu) {
				dispatch(updatePermissions(response.data.user.role.menu));
			}
		}
	};

	const onChangeTab: any = (e: any) => {
		if (e.key) {
			setCurrent(e.key.toString());
		}
	};
	const onOpenChange = (keys: any) => {
		const latestOpenKey = keys.find((key: any) => openKeys.indexOf(key) === -1);
		if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
			setOpenKeys(keys);
		} else {
			setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
		}
	};

	const updateSideBarTab = (current_: string, key_: string) => {
		handleChangeHeaderTitle(current_);
		setCurrent(current_);
		setOpenKeys([key_]);
	};

	// eslint-disable-next-line no-unused-vars
	const signOut = async () => {
		await Auth.signOut();
		window.location.href = process.env.REACT_APP_LOGIN_ROUTE || "";
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		getCurrentLoggedUser();
		const headerTitle = localStorage.getItem("headerTitle");
		setCurrent(headerTitle);
		if (
			headerTitle === "roles" ||
			headerTitle === "organizations" ||
			headerTitle === "users"
		) {
			setOpenKeys(["organizationManagement"]);
		} else if (
			headerTitle === "servers" ||
			headerTitle === "banks" ||
			headerTitle === "lifts" ||
			headerTitle === "adapters" ||
			headerTitle === "configurationCatalog" ||
			headerTitle === "channels"
		) {
			setOpenKeys(["edgeManagement"]);
		} else if (
			headerTitle === "rootPermissions" ||
			headerTitle === "offsetTypeConfiguration" ||
			headerTitle === "specialSwitches" ||
			headerTitle === "specialSwitchesCatalog" ||
			headerTitle === "dataBlocks" ||
			headerTitle === "vendors" ||
			headerTitle === "icon-definition" ||
			headerTitle === "permissions"
		) {
			setOpenKeys(["tools"]);
		} else if (
			headerTitle === "reportGeneral" ||
			headerTitle === "reportFault" ||
			headerTitle === "reportTraffic" ||
			headerTitle === "reportCarUsage" ||
			headerTitle === "reportService" ||
			headerTitle === "reportBeneficial" ||
			headerTitle === "reportPerformance" ||
			headerTitle === "reportOutOfService"
		) {
			setOpenKeys(["reports"]);
		}
	}, []);

	useEffect(() => {
		if (localStorage.getItem("headerTitle") === "monitoring") {
			setCurrent("monitoring");
		}
	}, [localStorage.getItem("headerTitle")]);

	useEffect(() => {
		if (location && location.pathname) {
			switch (location.pathname) {
				case "/monitoring":
					updateSideBarTab("monitoring", "monitoring");
					break;
				case "/map-view/map":
					updateSideBarTab("map", "mapView");
					break;
				case "/organization-management/roles":
					updateSideBarTab("roles", "organizationManagement");
					break;
				case "/organization-management/organizations":
					updateSideBarTab("organizations", "organizationManagement");
					break;
				case "/organization-management/users":
					updateSideBarTab("users", "organizationManagement");
					break;
				case "/edge-management/servers":
					updateSideBarTab("servers", "edgeManagement");
					break;
				case "/edge-management/adapters":
					updateSideBarTab("adapters", "edgeManagement");
					break;
				case "/edge-management/channels":
					updateSideBarTab("channels", "edgeManagement");
					break;
				case "/edge-management/banks":
					updateSideBarTab("banks", "edgeManagement");
					break;
				case "/edge-management/lifts":
					updateSideBarTab("lifts", "edgeManagement");
					break;
				case "/edge-management/configuration-catalog":
					updateSideBarTab("configurationCatalog", "edgeManagement");
					break;
				case "/tools/root-permissions":
					updateSideBarTab("rootPermissions", "tools");
					break;
				case "/tools/offset-type-configuration":
					updateSideBarTab("offsetTypeConfiguration", "tools");
					break;
				case "/tools/special-switches-configuration":
					updateSideBarTab("specialSwitchesConfiguration", "tools");
					break;
				case "/tools/special-switches-catalog-configuration":
					updateSideBarTab("specialSwitchesCatalogConfiguration", "tools");
					break;
				case "/tools/data-blocks-configuration":
					updateSideBarTab("dataBlocksConfiguration", "tools");
					break;
				case "/tools/vendors":
					updateSideBarTab("vendorsConfiguration", "tools");
					break;
				case "/tools/icon-definition":
					updateSideBarTab("iconDefinitionConfiguration", "tools");
					break;
				case "/tools/permissions":
					updateSideBarTab("permissions", "tools");
					break;
				case "/reports/report-general":
					updateSideBarTab("reportGeneral", "reports");
					break;
				case "/reports/report-fault":
					updateSideBarTab("reportFault", "reports");
					break;
				case "/reports/report-performance":
					updateSideBarTab("reportPerformance", "reports");
					break;
				case "/reports/report-out-of-service":
					updateSideBarTab("reportOutOfService", "reports");
					break;
				case "/reports/report-traffic":
					updateSideBarTab("reportTraffic", "reports");
					break;
				case "/reports/report-car-user":
					updateSideBarTab("reportCarUsage", "reports");
					break;
				case "/reports/report-service":
					updateSideBarTab("reportService", "reports");
					break;
				case "/reports/report-beneficial":
					updateSideBarTab("reportBeneficial", "reports");
					break;
				default:
					break;
			}
		}
	}, [location]);

	useEffect(() => {
		showIsLoading();
		setTimeout(() => {
			setLocalShowFullView(showFullView);
		}, 100);
	}, [showFullView]);

	useEffect(() => {
		if (getCurrentLoggedUserIsError) {
			// signOut();
		}
	}, [getCurrentLoggedUserIsError]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Spin spinning={isLoading || getCurrentLoggedUserIsLoading}>
			{!isLoading && (
				<Layout className='generalStyles__layoutHeight'>
					{/** ******************************************************* */}
					{/** SIDE BAR */}
					{((!localShowFullView &&
						localStorage.getItem("headerTitle") === "monitoring") ||
						localStorage.getItem("headerTitle") !== "monitoring") && (
						<Sider
							collapsible
							collapsed={collapsed || isMobile}
							onCollapse={(value) => setCollapsed(value)}
							width='290'
						>
							<div className='logo'>
								<img
									src={
										isMobile
											? mobileLogo
											: theme === "light"
											? logoWhite
											: logoBlack
									}
									alt=''
									className={
										isMobile
											? "layout__sideBarMobileLogo"
											: "layout__sideBarLogo"
									}
								/>
							</div>
							<Menu
								defaultSelectedKeys={[current]}
								defaultOpenKeys={openKeys}
								selectedKeys={[current]}
								openKeys={openKeys}
								mode='inline'
								onClick={onChangeTab}
								onOpenChange={onOpenChange}
							>
								{/* -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*- */}
								{/** MONITORING *********** */}
								{validatePermission("monitor-dashboard", permissions) && (
									<Menu.Item
										key='monitoring'
										icon={
											<FontAwesomeIcon
												className='generalStyles__info'
												icon={faChartGantt}
											/>
										}
									>
										<Link
											onClick={() => {
												handleChangeHeaderTitle("monitoring");
											}}
											to='/monitoring'
										>
											<span>{t("sideBar.monitoring")}</span>
										</Link>
									</Menu.Item>
								)}
								{/* -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*- */}
								{/** MAP VIEW *********** */}
								{showMapView && (
									<Menu.SubMenu
										title={t("sideBar.mapView")}
										key='mapView'
										icon={
											<FontAwesomeIcon
												className='generalStyles__info'
												icon={faBuildingUser}
											/>
										}
									>
										<Menu.Item
											key='map'
											icon={
												<FontAwesomeIcon
													className='generalStyles__info'
													icon={faBuilding}
												/>
											}
										>
											<Link
												onClick={() => {
													handleChangeHeaderTitle("mapView");
												}}
												to='/map-view/map'
											>
												<span>{t("sideBar.mapView")}</span>
											</Link>
										</Menu.Item>
									</Menu.SubMenu>
								)}
								{/* -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*- */}
								{/** ORGANIZATION MANAGEMENT *********** */}
								{validatePermission("organization-management", permissions) && (
									<Menu.SubMenu
										title={t("sideBar.organizationManagement")}
										key='organizationManagement'
										icon={
											<FontAwesomeIcon
												className='generalStyles__info'
												icon={faBuildingUser}
											/>
										}
									>
										{/** ORGANIZATIONS *********** */}
										{validatePermission("organizations", permissions) && (
											<Menu.Item
												key='organizations'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faBuilding}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("organizations");
													}}
													to='/organization-management/organizations'
												>
													<span>{t("sideBar.organizations")}</span>
												</Link>
											</Menu.Item>
										)}
										{/** ROLES AND PERMISSIONS *********** */}
										{validatePermission("roles", permissions) && (
											<Menu.Item
												key='roles'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faUserShield}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("roles");
													}}
													to='/organization-management/roles'
												>
													<span>{t("sideBar.roles")}</span>
												</Link>
											</Menu.Item>
										)}
										{/** USERS *********** */}
										{validatePermission("users", permissions) && (
											<Menu.Item
												key='users'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faUsers}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("users");
													}}
													to='/users-management/users'
												>
													<span>{t("sideBar.users")}</span>
												</Link>
											</Menu.Item>
										)}
									</Menu.SubMenu>
								)}
								{/* -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*- */}
								{/** EDGE MANAGEMENT *********** */}
								{validatePermission("edge-management", permissions) && (
									<Menu.SubMenu
										title={t("header.edgeManagement")}
										key='edgeManagement'
										icon={
											<FontAwesomeIcon
												className='generalStyles__info'
												icon={faGears}
											/>
										}
									>
										{/** SERVERS *********** */}
										{validatePermission("servers", permissions) && (
											<Menu.Item
												key='servers'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faServer}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("servers");
													}}
													to='/edge-management/servers'
												>
													<span>{t("sideBar.servers")}</span>
												</Link>
											</Menu.Item>
										)}
										{/** CHANNELS *********** */}
										{validatePermission("channels", permissions) && (
											<Menu.Item
												key='channels'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faEthernet}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("channels");
														dispatch(updateBreadcrumbTitle("channels"));
														dispatch(
															updateServer({
																id: "",
																ip: "",
																name: "",
																next_records_sync_run: "",
																next_server_sync_run: "",
																org_id: "",
																record_sync_size: 0,
																synced_at: "",
																sync_sched: "",
																channels: [],
															})
														);
														dispatch(updateRenderComponent("channels"));
													}}
													to='/edge-management/channels'
												>
													<span>{t("sideBar.channels")}</span>
												</Link>
											</Menu.Item>
										)}
										{/** BANKS *********** */}
										{validatePermission("banks", permissions) && (
											<Menu.Item
												key='banks'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faBuildingShield}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("banks");
													}}
													to='/edge-management/banks'
												>
													<span>{t("sideBar.banks")}</span>
												</Link>
											</Menu.Item>
										)}
										{/** LIFTS *********** */}
										{validatePermission("lifts", permissions) && (
											<Menu.Item
												key='lifts'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faElevator}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("lifts");
													}}
													to='/edge-management/lifts'
												>
													<span>{t("sideBar.lifts")}</span>
												</Link>
											</Menu.Item>
										)}
										{/** ADAPTERS *********** */}
										{validatePermission("adapters", permissions) && (
											<Menu.Item
												key='adapters'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faPlugCircleCheck}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("adapters");
													}}
													to='/edge-management/adapters'
												>
													<span>{t("sideBar.adapters")}</span>
												</Link>
											</Menu.Item>
										)}

										{/** CONFIGURATION CATALOG *********** */}
										{validatePermission("configurations", permissions) && (
											<Menu.Item
												key='configurationCatalog'
												icon={
													<FontAwesomeIcon
														className='generalStyles__info'
														icon={faFileCircleCheck}
													/>
												}
											>
												<Link
													onClick={() => {
														handleChangeHeaderTitle("configurationCatalog");
													}}
													to='/edge-management/configuration-catalog'
												>
													<span>{t("sideBar.configurationCatalog")}</span>
												</Link>
											</Menu.Item>
										)}
									</Menu.SubMenu>
								)}
								{/* -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*- */}
								{/** REPORTS *********** */}
								{validatePermission("reports", permissions) && (
									<Menu.SubMenu
										title='Reports'
										key='reports'
										icon={
											<FontAwesomeIcon
												className='generalStyles__info'
												icon={faFileCircleCheck}
											/>
										}
									>
										{validatePermission("general-report", permissions) && (
											<>
												{/** GENERAL REPORTS *********** */}
												<Menu.Item
													key='reportGeneral'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportGeneral");
														}}
														to='/reports/report-general'
													>
														<span>{t("sideBar.generalReports")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("fault-report", permissions) && (
											<>
												{/** REPORT FAULT *********** */}
												<Menu.Item
													key='reportFault'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportFault");
														}}
														to='/reports/report-fault'
													>
														<span>{t("sideBar.fault")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("traffic-report", permissions) && (
											<>
												{/** REPORT TRAFFIC *********** */}
												<Menu.Item
													key='reportTraffic'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportTraffic");
														}}
														to='/reports/report-traffic'
													>
														<span>{t("sideBar.traffic")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("car-report", permissions) && (
											<>
												{/** REPORT CAR USER *********** */}
												<Menu.Item
													key='reportCarUsage'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportCarUsage");
														}}
														to='/reports/report-car-user'
													>
														<span>{t("sideBar.carUsage")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("service-report", permissions) && (
											<>
												{/** REPORT SERVICE *********** */}
												<Menu.Item
													key='reportService'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportService");
														}}
														to='/reports/report-service'
													>
														<span>{t("sideBar.service")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("beneficial-report", permissions) && (
											<>
												{/** REPORT BENEFICIAL *********** */}
												<Menu.Item
													key='reportBeneficial'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportBeneficial");
														}}
														to='/reports/report-beneficial'
													>
														<span>{t("sideBar.beneficial")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("performance-report", permissions) && (
											<>
												{/** REPORT OUT OF SERVICE *********** */}
												<Menu.Item
													key='reportPerformance'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportPerformance");
														}}
														to='/reports/report-performance'
													>
														<span>{t("sideBar.performance")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission(
											"out-of-service-report",
											permissions
										) && (
											<>
												{/** REPORT OUT OF SERVICE *********** */}
												<Menu.Item
													key='reportOutOfService'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faFileArchive}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("reportOutOfService");
														}}
														to='/reports/report-out-of-service'
													>
														<span>{t("sideBar.outOfService")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
									</Menu.SubMenu>
								)}
								{/* -*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*-*- */}
								{/** TOOLS *********** */}
								{validatePermission("tools", permissions) && (
									<Menu.SubMenu
										title='Tools'
										key='tools'
										icon={
											<FontAwesomeIcon
												className='generalStyles__info'
												icon={faTools}
											/>
										}
									>
										{validatePermission("root-permissions", permissions) && (
											<>
												{/** ROOT PERMISSIONS *********** */}
												<Menu.Item
													key='rootPermissions'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("rootPermissions");
														}}
														to='/tools/root-permissions'
													>
														<span>{t("sideBar.rootPermissions")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission(
											"offset-types-catalog",
											permissions
										) && (
											<>
												{/** OFFSET TYPE CONFIGURATION *********** */}
												<Menu.Item
													key='offsetTypeConfiguration'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle(
																"offsetTypeConfiguration"
															);
														}}
														to='/tools/offset-type-configuration'
													>
														<span>{t("sideBar.offsetTypeConfiguration")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("special-switches", permissions) && (
											<>
												{/** SPECIAL SWITCHES CONFIGURATION *********** */}
												<Menu.Item
													key='specialSwitchesConfiguration'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle(
																"specialSwitchesConfiguration"
															);
														}}
														to='/tools/special-switches-configuration'
													>
														<span>
															{t("sideBar.specialSwitchesConfiguration")}
														</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission(
											"special-switches-catalog",
											permissions
										) && (
											<>
												{/** SPECIAL SWITCHES CATALOG CONFIGURATION *********** */}
												<Menu.Item
													key='specialSwitchesCatalogConfiguration'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle(
																"specialSwitchesCatalogConfiguration"
															);
														}}
														to='/tools/special-switches-catalog-configuration'
													>
														<span>
															{t("sideBar.specialSwitchesCatalogConfiguration")}
														</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("data-blocks-catalog", permissions) && (
											<>
												{/** DATA BLOCKS CONFIGURATION *********** */}
												<Menu.Item
													key='dataBlocksConfiguration'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle(
																"dataBlocksConfiguration"
															);
														}}
														to='/tools/data-blocks-configuration'
													>
														<span>{t("sideBar.dataBlocks")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission("vendors-catalog", permissions) && (
											<>
												{/** VENDORS CONFIGURATION *********** */}
												<Menu.Item
													key='vendorsConfiguration'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("vendorsConfiguration");
														}}
														to='/tools/vendors'
													>
														<span>{t("sideBar.vendors")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission(
											"permission-not-defined",
											permissions
										) && (
											<>
												{/** ICON DEFINITION CONFIGURATION *********** */}
												<Menu.Item
													key='iconDefinitionConfiguration'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle(
																"iconDefinitionConfiguration"
															);
														}}
														to='/tools/icon-definition'
													>
														<span>{t("sideBar.iconDefinition")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
										{validatePermission(
											"permission-not-defined",
											permissions
										) && (
											<>
												{/** ICON DEFINITION CONFIGURATION *********** */}
												<Menu.Item
													key='permissions'
													icon={
														<FontAwesomeIcon
															className='generalStyles__info'
															icon={faShieldHalved}
														/>
													}
												>
													<Link
														onClick={() => {
															handleChangeHeaderTitle("permissions");
														}}
														to='/tools/permissions'
													>
														<span>{t("sideBar.permissions")}</span>
													</Link>
												</Menu.Item>
											</>
										)}
									</Menu.SubMenu>
								)}
							</Menu>
						</Sider>
					)}
					{/** ******************************************************* */}
					{/** LAYOUT */}
					<Layout
						style={{
							overflowX: "auto",
						}}
					>
						{/** ******************************************************* */}
						{/** HEADER */}
						{((!localShowFullView &&
							localStorage.getItem("headerTitle") === "monitoring") ||
							localStorage.getItem("headerTitle") !== "monitoring") && (
							<Header className='generalStyles__header'>
								<div className='generalStyles__spaceBetween'>
									<div />
									<div>
										<YourProfileDrawer />
									</div>
								</div>
							</Header>
						)}
						{/** ******************************************************* */}
						{/** CONTENT */}
						<Content>
							<LayoutBreadcrumb />
							<div
								className={
									isMobile ? "layout__contentPageMobile" : "layout__contentPage"
								}
							>
								<Routes>
									<Route path='*' element={<NotFoundPage />} />
									<Route path='' element={<MonitoringPage />} />
									<Route path='monitoring' element={<MonitoringPage />} />
									<Route path='map' element={<MapViewPage />} />
									<Route path='users' element={<UsersPage />} />
									<Route path='organizations' element={<OrganizationsPage />} />
									<Route path='roles' element={<RolesPage />} />
									<Route
										path='root-permissions'
										element={<RootPermissionsPage />}
									/>
									<Route
										path='offset-type-configuration'
										element={<OffsetTypePage />}
									/>
									<Route
										path='special-switches-configuration'
										element={<SpecialSwitchesPage />}
									/>
									<Route
										path='special-switches-catalog-configuration'
										element={<SpecialSwitchesCatalogPage />}
									/>
									<Route
										path='data-blocks-configuration'
										element={<DataBlocksPage />}
									/>
									<Route path='vendors' element={<VendorsPage />} />
									<Route
										path='icon-definition'
										element={<IconDefinitionPage />}
									/>
									<Route path='permissions' element={<PermissionsPage />} />
									<Route
										path='configuration-catalog'
										element={<ConfigurationFiles />}
									/>
									<Route
										path='configuration-catalog/definitions'
										element={<DefinitionsPage />}
									/>
									<Route
										path='configuration-catalog/offsets'
										element={<OffsetsPage />}
									/>
									<Route path='banks' element={<BanksPage />} />
									<Route path='servers' element={<ServersPage />} />
									<Route path='lifts' element={<LiftsPage />} />
									<Route path='adapters' element={<AdaptersPage />} />
									<Route path='servers/channels' element={<ChannelsPage />} />
									<Route path='channels' element={<ChannelsPage />} />
									<Route
										path='report-general'
										element={<ReportGeneralPage />}
									/>
									<Route path='report-fault' element={<ReportFaultPage />} />
									<Route
										path='report-performance'
										element={<ReportPerformancePage />}
									/>
									<Route
										path='report-out-of-service'
										element={<ReportOutOfServicePage />}
									/>
									<Route
										path='report-traffic'
										element={<ReportTrafficPage />}
									/>
									<Route
										path='report-car-user'
										element={<ReportCarUsagePage />}
									/>
									<Route
										path='report-service'
										element={<ReportServicePage />}
									/>
									<Route
										path='report-beneficial'
										element={<ReportBeneficialPage />}
									/>
								</Routes>
								<GlobalAlert />
							</div>
						</Content>
						{/** ******************************************************* */}
						{/** FOOTER */}
						<Footer className='layout__footerText'>
							<span
								className={
									isMobile
										? "layout__footerTextMobile"
										: "layout__footerTextDesktop"
								}
							>
								© {new Date().getFullYear()} powered by - LiftNet
							</span>
						</Footer>
					</Layout>
				</Layout>
			)}
			{!isLoading &&
				!getCurrentLoggedUserIsLoading &&
				userLogged &&
				userLogged.id && <TermsAndConditions />}
			{isLoading && <div style={{ height: "50vh" }} />}
		</Spin>
	);
};

export default App;
